// SHADOW MARKET™ — Wallet comparator component (category filter + cards + modal).
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Smile, Tag, X, ExternalLink, Lock, Filter, Star } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { WALLETS, CATEGORIAS, CATEGORIAS_LABELS, tx } from "../data/walletsComparador";
import { getComparadorUi } from "../i18n/comparador-wallets";
import LedgerComparadorBtn from "./topwallet/LedgerComparadorBtn";
import TrezorComparadorBtn from "./topwallet/TrezorComparadorBtn";

const Stars = ({ n, max = 5, color = "#f5a623" }) => (
  <span className="inline-flex gap-0.5">
    {Array.from({ length: max }, (_, i) => (
      <Star key={i} className="h-3 w-3" style={{ color, fill: i < n ? color : "transparent" }} />
    ))}
  </span>
);

const Chip = ({ children, color = "rgba(255,255,255,0.1)", textColor = "rgba(255,255,255,0.7)" }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em]"
    style={{ borderColor: color, color: textColor, background: `${color}22` }}
  >
    {children}
  </span>
);

// Map Ledger wallet IDs to their affiliate product URLs (tracker = comparador_wallets).
const LEDGER_AFFILIATE_LINKS = {
  "ledger-nano-s-plus":
    "https://shop.ledger.com/products/ledger-nano-s-plus?r=eb740c0da9e0&tracker=comparador_wallets",
  "ledger-nano-x":
    "https://shop.ledger.com/products/ledger-nano-x?r=eb740c0da9e0&tracker=comparador_wallets",
  "ledger-stax":
    "https://shop.ledger.com/products/ledger-stax?r=eb740c0da9e0&tracker=comparador_wallets",
  "ledger-flex":
    "https://shop.ledger.com/products/ledger-flex?r=eb740c0da9e0&tracker=comparador_wallets",
};

const getLedgerAffiliateLink = (walletId) =>
  LEDGER_AFFILIATE_LINKS[walletId] ||
  "https://shop.ledger.com/?r=eb740c0da9e0&tracker=comparador_wallets";

// Direct Trezor affiliate links (aff_id=159881).
const TREZOR_AFFILIATE_LINKS = {
  "trezor-model-one": "https://trezor.io/trezor-model-one?offer_id=1&aff_id=159881",
  "trezor-safe-3": "https://trezor.io/trezor-safe-3?offer_id=1&aff_id=159881",
  "trezor-model-t": "https://trezor.io/trezor-model-t?offer_id=1&aff_id=159881",
  "trezor-safe-5": "https://trezor.io/trezor-safe-5?offer_id=1&aff_id=159881",
};

const getTrezorAffiliateLink = (walletId) =>
  TREZOR_AFFILIATE_LINKS[walletId] || "https://trezor.io/?aff_id=159881";

export const ComparadorWallets = () => {
  const { lang } = useLanguage();
  const ui = getComparadorUi(lang);

  const [catFiltro, setCatFiltro] = useState("all");
  const [tipoFiltro, setTipoFiltro] = useState("all");
  const [sortBy, setSortBy] = useState("security");
  const [selected, setSelected] = useState(null);

  const visible = useMemo(() => {
    let out = WALLETS.filter(
      (w) =>
        (catFiltro === "all" || w.categoria === catFiltro) &&
        (tipoFiltro === "all" || w.tipo === tipoFiltro)
    );
    if (sortBy === "security") out.sort((a, b) => b.seguridad - a.seguridad);
    else if (sortBy === "ease") out.sort((a, b) => b.facilidad - a.facilidad);
    else if (sortBy === "price_asc") out.sort((a, b) => a.precio - b.precio);
    else if (sortBy === "price_desc") out.sort((a, b) => b.precio - a.precio);
    return out;
  }, [catFiltro, tipoFiltro, sortBy]);

  return (
    <div data-testid="comparador-wallets" className="w-full">
      {/* Category filter row */}
      <div className="mb-3 flex flex-wrap gap-2" data-testid="comparador-cat-filter">
        {CATEGORIAS.map((c) => {
          const active = catFiltro === c;
          return (
            <button
              key={c}
              onClick={() => setCatFiltro(c)}
              data-testid={`comparador-cat-${c}`}
              className={`rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-colors ${
                active
                  ? "border-blue-400/60 bg-blue-400/15 text-blue-200"
                  : "border-white/10 bg-white/[0.03] text-white/65 hover:border-blue-400/30 hover:text-white"
              }`}
            >
              {tx(CATEGORIAS_LABELS[c], lang)}
            </button>
          );
        })}
      </div>

      {/* Type filter + sort */}
      <div className="mb-4 flex flex-wrap gap-2">
        {[
          { v: "all", label: ui.type_all },
          { v: "cold", label: ui.type_cold },
          { v: "hot", label: ui.type_hot },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTipoFiltro(t.v)}
            data-testid={`comparador-tipo-${t.v}`}
            className={`rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-colors ${
              tipoFiltro === t.v
                ? "border-orange-400/60 bg-orange-400/15 text-orange-200"
                : "border-white/10 bg-white/[0.03] text-white/65 hover:border-orange-400/30"
            }`}
          >
            {t.label}
          </button>
        ))}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="comparador-sort"
          className="ml-auto rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/80"
        >
          <option value="security">{ui.sort_security}</option>
          <option value="ease">{ui.sort_ease}</option>
          <option value="price_asc">{ui.sort_price_asc}</option>
          <option value="price_desc">{ui.sort_price_desc}</option>
        </select>
      </div>

      <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">
        {ui.showing(visible.length, WALLETS.length)}
      </p>

      {visible.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
          <p className="mb-3 text-sm text-white/65">{ui.no_results}</p>
          <button
            onClick={() => { setCatFiltro("all"); setTipoFiltro("all"); }}
            className="rounded-xl bg-blue-500/20 px-4 py-2 text-xs font-bold uppercase tracking-widest text-blue-200"
          >
            {ui.reset}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="comparador-grid">
          {visible.map((w) => (
            <motion.button
              key={w.id}
              layout
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -3 }}
              onClick={() => setSelected(w)}
              data-testid={`comparador-card-${w.id}`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left backdrop-blur-xl transition-colors hover:border-blue-400/40"
              style={{ borderColor: `${w.colorAcento}30` }}
            >
              {w.badge && (
                <span
                  className="absolute right-3 top-3 rounded-full border px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.18em]"
                  style={{ borderColor: w.colorAcento + "60", background: w.colorAcento + "20", color: w.colorAcento }}
                >
                  {tx(w.badge, lang)}
                </span>
              )}
              <div className="flex items-center gap-3">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl"
                  style={{ background: `${w.colorAcento}15`, border: `1px solid ${w.colorAcento}55` }}
                >
                  {w.logo}
                </div>
                <div className="min-w-0">
                  <div className="text-base font-black leading-tight text-white">{w.nombre}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    <Chip color={w.tipo === "cold" ? "rgba(0,212,255,0.55)" : "rgba(245,166,35,0.55)"} textColor={w.tipo === "cold" ? "#00d4ff" : "#f5a623"}>
                      {w.tipo === "cold" ? ui.cold : ui.hot}
                    </Chip>
                    <Chip color="rgba(255,255,255,0.18)" textColor="rgba(255,255,255,0.65)">
                      {tx(CATEGORIAS_LABELS[w.categoria], lang).replace(/^[^\s]+\s/, "")}
                    </Chip>
                  </div>
                </div>
              </div>
              <p className="mt-2 line-clamp-2 text-xs text-white/55">{tx(w.mejorPara, lang)}</p>
              <div className="mt-3 grid grid-cols-3 gap-1.5">
                <div className="rounded-md border border-white/5 bg-white/[0.03] px-2 py-1.5">
                  <div className="text-[8px] font-bold uppercase tracking-widest text-white/40">{ui.security}</div>
                  <Stars n={w.seguridad} color="#ef4444" />
                </div>
                <div className="rounded-md border border-white/5 bg-white/[0.03] px-2 py-1.5">
                  <div className="text-[8px] font-bold uppercase tracking-widest text-white/40">{ui.ease}</div>
                  <Stars n={w.facilidad} color="#00d4ff" />
                </div>
                <div className="rounded-md border border-white/5 bg-white/[0.03] px-2 py-1.5">
                  <div className="text-[8px] font-bold uppercase tracking-widest text-white/40">{ui.price}</div>
                  <div className="font-mono text-xs font-black" style={{ color: w.precio === 0 ? "#00e676" : w.colorAcento }}>
                    {w.precio === 0 ? ui.free_short : `€${w.precio}`}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {w.defi && <Chip>{ui.badges.defi}</Chip>}
                {w.mobile && <Chip>{ui.badges.mobile}</Chip>}
                {w.desktop && <Chip>{ui.badges.desktop}</Chip>}
                {w.nfc && <Chip>{ui.badges.nfc}</Chip>}
                {w.openSource && <Chip color="rgba(0,230,118,0.35)" textColor="#00e676">OS</Chip>}
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {/* Detail modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-md md:items-center md:p-6"
            onClick={() => setSelected(null)}
            data-testid="comparador-modal"
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl border border-white/10 bg-gradient-to-br from-zinc-950 to-black p-6 md:rounded-3xl"
              style={{ borderColor: `${selected.colorAcento}55` }}
            >
              <button
                onClick={() => setSelected(null)}
                aria-label={ui.close}
                className="absolute right-4 top-4 rounded-full border border-white/10 bg-white/[0.05] p-2 text-white/70 hover:text-white"
                data-testid="comparador-modal-close"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="mb-4 flex items-center gap-4">
                <div
                  className="flex h-16 w-16 items-center justify-center rounded-2xl text-3xl"
                  style={{ background: `${selected.colorAcento}15`, border: `1px solid ${selected.colorAcento}55` }}
                >
                  {selected.logo}
                </div>
                <div>
                  <h3 className="text-xl font-black text-white">{selected.nombre}</h3>
                  <p className="text-xs text-white/60">{tx(selected.mejorPara, lang)}</p>
                </div>
              </div>

              <div className="mb-4 grid grid-cols-3 gap-2">
                <Cell title={ui.security} icon={Shield} value={<Stars n={selected.seguridad} color="#ef4444" />} />
                <Cell title={ui.ease} icon={Smile} value={<Stars n={selected.facilidad} color="#00d4ff" />} />
                <Cell
                  title={ui.price}
                  icon={Tag}
                  value={
                    <span className="font-mono text-base font-black" style={{ color: selected.precio === 0 ? "#00e676" : selected.colorAcento }}>
                      {selected.precio === 0 ? ui.free : `€${selected.precio}`}
                    </span>
                  }
                />
              </div>

              <p className="mb-4 text-sm leading-relaxed text-white/80">{tx(selected.descripcion, lang)}</p>

              <Section title={ui.chains}>
                <div className="flex flex-wrap gap-1.5">
                  {selected.cadenas.map((c) => (
                    <Chip key={c} color={`${selected.colorAcento}55`} textColor={selected.colorAcento}>
                      {c}
                    </Chip>
                  ))}
                </div>
              </Section>

              <Section title={ui.pros}>
                <ul className="space-y-1">
                  {tx(selected.ventajas, lang).map((v, i) => (
                    <li key={i} className="text-sm text-white/75">
                      <span className="mr-2 text-emerald-300">✓</span>{v}
                    </li>
                  ))}
                </ul>
              </Section>

              <Section title={ui.cons}>
                <ul className="space-y-1">
                  {tx(selected.desventajas, lang).map((v, i) => (
                    <li key={i} className="text-sm text-white/75">
                      <span className="mr-2 text-orange-300">!</span>{v}
                    </li>
                  ))}
                </ul>
              </Section>

              <div className="mt-4 flex flex-wrap gap-2">
                {selected.openSource ? (
                  <Chip color="rgba(0,230,118,0.45)" textColor="#00e676">{ui.open_source}</Chip>
                ) : (
                  <Chip color="rgba(255,255,255,0.18)" textColor="rgba(255,255,255,0.6)"><Lock className="h-2.5 w-2.5" /> {ui.closed}</Chip>
                )}
                {selected.defi && <Chip>{ui.badges.defi}</Chip>}
                {selected.mobile && <Chip>{ui.badges.mobile}</Chip>}
                {selected.desktop && <Chip>{ui.badges.desktop}</Chip>}
                {selected.nfc && <Chip>{ui.badges.nfc}</Chip>}
                {selected.hardware && <Chip color="rgba(0,212,255,0.45)" textColor="#00d4ff">{ui.badges.hw}</Chip>}
              </div>

              <a
                href={
                  selected.id?.startsWith("ledger-")
                    ? getLedgerAffiliateLink(selected.id)
                    : selected.id?.startsWith("trezor-")
                      ? getTrezorAffiliateLink(selected.id)
                      : selected.url
                }
                target="_blank"
                rel={
                  selected.id?.startsWith("ledger-") || selected.id?.startsWith("trezor-")
                    ? "noopener noreferrer sponsored"
                    : "noopener noreferrer"
                }
                data-testid="comparador-modal-visit"
                className="mt-5 inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-black uppercase tracking-widest text-black"
                style={{ background: `linear-gradient(135deg, ${selected.colorAcento}, ${selected.colorAcento}dd)`, boxShadow: `0 0 25px ${selected.colorAcento}55` }}
              >
                {ui.visit} <ExternalLink className="h-3.5 w-3.5" />
              </a>

              {/* Affiliate CTA for Ledger products (additive — does not replace the generic CTA above) */}
              {selected.id?.startsWith("ledger-") && (
                <div data-testid="comparador-modal-ledger-affiliate" className="mt-3">
                  <LedgerComparadorBtn walletId={selected.id} lang={lang} />
                </div>
              )}

              {/* Affiliate CTA for Trezor products (additive — sibling of Ledger above) */}
              {selected.id?.startsWith("trezor-") && (
                <div data-testid="comparador-modal-trezor-affiliate" className="mt-3">
                  <TrezorComparadorBtn walletId={selected.id} lang={lang} />
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Cell = ({ title, icon: Icon, value }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
    <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-white/45">
      <Icon className="h-3 w-3" /> {title}
    </div>
    <div className="mt-1">{value}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
    <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-white/45">{title}</div>
    {children}
  </div>
);

export default ComparadorWallets;
